import { useState } from "react"
import { post } from "./api"

/**
 * Talk to us — the ask that is not a capture.
 *
 * The capture points across this product trade an address for something the visitor already wanted:
 * a report, a profile, a drop. This is the other conversation — somebody with a question, who needs
 * room to say what it is — and the `note` is the whole point. An email address with no sentence
 * attached is a lead you have to open a conversation to qualify, which at this size means it gets
 * answered three days later.
 *
 * SHARED, because landing and gtm both want it and neither owns it. A vendor arriving from a risk
 * profile that names their product has the most urgent question anybody on this site has, and making
 * them navigate to the marketing surface to ask it would be the wrong place to put a wall.
 *
 * WHERE IT GOES. `POST /contact` on agitome's API, which forwards it server-side to authoxi's leads
 * plane — authoxi owns the portfolio's leads table and the only mail path in the group. The browser
 * never sees authoxi: putting a second product's API in this bundle would mean widening authoxi's
 * CORS to agitome's origins and making its lead intake reachable from any page that copied the
 * snippet.
 *
 * THE FAILURE STATE IS VISIBLE, deliberately. authoxi's version of this form spent a period posting
 * to a route that did not exist, getting a 404, and falling back to a `mailto:` the visitor may
 * never have sent — silently, so nobody found out for weeks. A form that swallows its own error is
 * how you lose the leads you were counting, so a failure here says so and keeps what they typed.
 */
export function Contact({
  surface,
  heading = "Talk to us",
  blurb = "A question, a correction, or a page about your product you want to answer. A person reads these.",
}: {
  surface: string
  heading?: string
  blurb?: string
}) {
  const [email, setEmail] = useState("")
  const [note, setNote] = useState("")
  const [company, setCompany] = useState("")
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle")
  const [err, setErr] = useState<string | null>(null)

  if (state === "sent") {
    return (
      <div className="contact contact--done" data-testid="contact-done">
        <p className="h3">Got it — we will reply to {email}.</p>
        <p className="muted">
          Nothing was added to a mailing list. This is a conversation, not a signup.
        </p>
      </div>
    )
  }

  return (
    <form
      className="contact"
      data-testid="contact-form"
      onSubmit={async (e) => {
        e.preventDefault()
        setState("sending")
        setErr(null)
        try {
          await post("/contact", {
            email,
            surface,
            note: note || undefined,
            company: company || undefined,
          })
          setState("sent")
        } catch {
          // What they typed stays in the fields. Clearing a form on failure asks somebody to write
          // their question twice, and most people write it once.
          setState("idle")
          setErr("That did not go through — nothing was sent. Try again in a moment.")
        }
      }}
    >
      <h2 className="h2">{heading}</h2>
      <p className="muted">{blurb}</p>

      <label className="field">
        <span className="field__label">Your email</span>
        <input
          className="input"
          type="email"
          required
          placeholder="you@company.com"
          data-testid="contact-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">Company — optional</span>
        <input
          className="input"
          type="text"
          placeholder="Where you work"
          data-testid="contact-company"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">What would you like to ask?</span>
        <textarea
          className="input"
          rows={5}
          maxLength={2000}
          placeholder="A question, a use case, or a page about your product you want to answer."
          data-testid="contact-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>

      {err && (
        <p className="alert" role="alert" data-testid="contact-error">
          {err}
        </p>
      )}

      <button className="btn btn--primary" type="submit" disabled={state === "sending"}>
        {state === "sending" ? "Sending…" : "Send"}
      </button>
    </form>
  )
}
